import { put, call, delay, select } from 'redux-saga/effects'
import { get } from 'lodash'
import moment from 'moment'
import DisplayActions from '../redux/DisplayRedux'
import { history } from '../redux';
import { createCampaignProposals } from '../services/campaignProposalApi';
import { getDomain, getNameWhiteLabel, hasAccessRole } from '../services/utils';
import UserRoleEnum from '../constants/UserRoleEnum';


export function* checkDisplaysByIdsRequest(api, action) {
  const { params } = action;
  const { display_ids, ...restParams } = params;

  const resp = yield call(api.checkDisplayIds, { ...restParams, display_ids });

  if (resp.ok) {
    const { valid = [], invalid = [] } = resp.data;
    const cart = yield select(state => state.display.cart);
    const cartIdSet = new Set(cart.map(d => Number(d.id)));

    const newDisplays = valid.filter(d => !cartIdSet.has(Number(d.id)));
    const alreadyInCartDisplays = valid.filter(d => cartIdSet.has(Number(d.id)));
    const alreadyInCart = alreadyInCartDisplays.length;

    if (newDisplays.length > 0) {
      yield put(DisplayActions.addListDisplayToCart(newDisplays));
    }

    yield put(DisplayActions.checkDisplaysByIdsSuccess({
      added: newDisplays.length,
      addedIds: newDisplays.map(d => String(d.id)),
      alreadyInCart,
      alreadyInCartIds: alreadyInCartDisplays.map(d => String(d.id)),
      invalid,
      total: display_ids.length,
    }));
  } else {
    yield put(DisplayActions.checkDisplaysByIdsFailure([resp.problem]));
  }
}

export function* getDisplaysCartRequest(api, action) {
  const { params } = action

  // POST so the cart's `list_id` (all cart ids) travels in the body, not the URL.
  const resp = yield call(api.postDisplays, params)

  if (resp.ok) {
    const displays = resp.data.data
    yield put(DisplayActions.getDisplaysCartSuccess(displays))
  } else {
    let errors

    if (resp.problem === 'CLIENT_ERROR') {
      errors = [resp.data.message]
    }
    else {
      errors = [resp.problem]
    }

    yield put(DisplayActions.getDisplaysCartFailure(errors))
  }
}

function* resolveBundleMembers(api, params, displays) {
  const fetchedIds = new Set(displays.map(display => display.id))
  const bundleIds = []
  const missingIds = new Set()

  displays.forEach(display => {
    if (!display.bundle_only) return

    const bundles = display.bundles || []

    bundles.forEach(bundle => {
      bundleIds.push(bundle.id)

      const members = bundle.displays || []

      members.forEach(member => {
        if (fetchedIds.has(member.id)) return
        missingIds.add(member.id)
      })
    })
  })

  if (missingIds.size === 0) {
    return { displays, bundleIds, missingFailed: false }
  }

  const missingResp = yield call(api.postDisplays, {
    ...params,
    list_id: [...missingIds],
  })

  if (!missingResp.ok) {
    return { displays, bundleIds, missingFailed: true, missingResp }
  }

  return {
    displays: [
      ...displays,
      ...missingResp.data.data.filter(display => !fetchedIds.has(display.id)),
    ],
    bundleIds,
    missingFailed: false,
  }
}

export function* getBulkDisplaysCartRequest(api, action) {
  const { params } = action

  // POST so the cart's `list_id` (all cart ids) travels in the body, not the URL.
  const resp = yield call(api.postDisplays, params)

  if (resp.ok) {
    const { displays: cart, bundleIds, missingFailed, missingResp } =
      yield* resolveBundleMembers(api, params, resp.data.data)

    if (missingFailed) {
      yield put(
        DisplayActions.getBulkDisplaysCartFailure(
          missingResp.problem === 'CLIENT_ERROR'
            ? [missingResp.data.message]
            : [missingResp.problem]
        )
      )
      return
    }

    if (bundleIds.length > 0) {
      const listBundle = yield select(state => state.display.listBundle)

      // listBundle first, so it's already updated on the render where `fetching`
      // drops and the view can tell which bundles this bulk action brought in.
      yield put(
        DisplayActions.updateBundles([...new Set([...(listBundle || []), ...bundleIds])])
      )
    }

    yield put(DisplayActions.getBulkDisplaysCartSuccess(cart))
  } else {
    let errors

    if (resp.problem === 'CLIENT_ERROR') {
      errors = [resp.data.message]
    }
    else {
      errors = [resp.problem]
    }

    yield put(DisplayActions.getBulkDisplaysCartFailure(errors))
  }
}

const CLUSTER_POLYGON_BATCH_SIZE = 200

function chunkList(list, size) {
  const chunks = []
  for (let i = 0; i < list.length; i += size) {
    chunks.push(list.slice(i, i + size))
  }
  return chunks
}

export function* addClusterPolygonDisplaysRequest(api, action) {
  const { params } = action
  const { list_id, ...restParams } = params
  const batches = chunkList(list_id, CLUSTER_POLYGON_BATCH_SIZE)

  let displays = []
  let hadErrors = false

  for (let i = 0; i < batches.length; i++) {
    const resp = yield call(api.postDisplays, { ...restParams, list_id: batches[i] })

    if (resp.ok) {
      displays = displays.concat(resp.data.data)
    } else {
      hadErrors = true
    }
  }

  if (displays.length === 0) {
    yield put(DisplayActions.addClusterPolygonDisplaysFailure(['batch_failed']))
    return
  }

  const { displays: cart, bundleIds, missingFailed } =
    yield* resolveBundleMembers(api, restParams, displays)

  if (missingFailed) hadErrors = true

  if (bundleIds.length > 0) {
    const listBundle = yield select(state => state.display.listBundle)
    yield put(
      DisplayActions.updateBundles([...new Set([...(listBundle || []), ...bundleIds])])
    )
  }

  yield put(DisplayActions.addClusterPolygonDisplaysSuccess(cart, hadErrors))
}

export function* addDisplayToCartByIdRequest(api, action) {
  const { params } = action

  // POST so a large `list_id` (e.g. a polygon selecting many screens) goes in
  // the body instead of overflowing the URL.
  const resp = yield call(api.postDisplays, params)

  if (resp.ok) {
    const displays = resp.data.data
    if (displays && displays.length > 0) {
      yield put(DisplayActions.addDisplayToCart(displays[0]))
    }
  }
}

// TODO: review in next sprint
export function* getDisplayDetailRequest(api, action) {
  const { params } = action

  const resp = yield call(api.getDisplays, params)

  if (resp.ok) {
    const displays = resp.data.data
    if (displays && displays.length > 0) {
      yield put(DisplayActions.setDisplayDetail(displays[0]))
    }
  }
}

export function* getDisplaysRequest(api, action) {
  const { params } = action

  // Always POST: `list_id` can carry many ids and, sent over GET, would overflow
  // the URL length limit. POSTing to /displays/results keeps the ids in the body.
  const resp = yield call(api.postDisplays, params)

  if (resp.ok) {
    const displays = resp.data.data

    yield put(DisplayActions.getDisplaysSuccess(displays, resp.data.per_page, resp.data.total))
  } else {
    let errors

    if (resp.problem === 'CLIENT_ERROR') {
      errors = [resp.data.message]
    }
    else {
      errors = [resp.problem]
    }

    yield put(DisplayActions.getDisplaysFailure(errors))
  }
}

export function* getDisplaysMapRequest(api, action) {
  const { params } = action

  const resp = yield call(api.getDisplaysMap, params)

  if (resp.ok) {
    const mode = resp.data && resp.data.mode ? resp.data.mode : "detail"
    const displaysMap = resp.data && resp.data.data ? resp.data.data : (Array.isArray(resp.data) ? resp.data : [])
    const mediaOwners = (resp.data && resp.data["media-owners-list"]) || []
    // Taxonomy for the category/subcategory filters and labels; [] disables those filters.
    yield put(DisplayActions.setVenueTypes((resp.data && resp.data["venue-types-list"]) || []))
    /*
      mode can be "detail" or "cluster"
      if mode is "detail" the data is an array of displays with all the details
      if mode is "cluster" the data is an array of clusters, each cluster has a list of displays ids inside it.
      media-owners-list is the list of media owners whose displays fall in the current viewport.
    */
    yield put(DisplayActions.getDisplaysMapSuccess(displaysMap, mode, mediaOwners))
  } else {
    let errors

    if (resp.problem === 'CLIENT_ERROR') {
      errors = [resp.data.message]
    }
    else {
      errors = [resp.problem]
    }

    yield put(DisplayActions.getDisplaysMapFailure(errors))
  }
}

export function* getDisplayInfoRequest(api, action) {
  const { id, clientId, currency, invoice_issuing_country, external_dsp_id, is_guaranteed } = action

  // /displays/{id}/results brings no venue path nor catalog: labels derive the
  // path from the node id, so fetch the catalog once if this session has none.
  const venueTypes = yield select(state => state.display.venueTypes)
  if (!venueTypes.length) {
    const today = moment().format('YYYY-MM-DD')
    const catalogResp = yield call(api.postDisplays, { list_id: [id], date_from: today, date_to: today, currency, invoice_issuing_country })
    if (catalogResp.ok && catalogResp.data && catalogResp.data['venue-types-list']) {
      yield put(DisplayActions.setVenueTypes(catalogResp.data['venue-types-list']))
    }
  }

  const resp = yield call(api.getDisplayInfo, id, clientId, currency, invoice_issuing_country, external_dsp_id, is_guaranteed)

  if (resp.ok) {
    const display = resp.data
    yield put(DisplayActions.getDisplayInfoSuccess(display))
  } else {
    let errors

    if (resp.problem === 'CLIENT_ERROR') {
      errors = [resp.data.message]
    }
    else {
      errors = [resp.problem]
    }

    yield put(DisplayActions.getDisplayInfoFailure(errors))
  }
}

export function* getDisplaysInfoRequest(api, action) {
  const { params } = action

  // POST so `list_id` (a campaign's displays) goes in the body, not the URL.
  const resp = yield call(api.postDisplays, params)

  if (resp.ok) {

    const displays = resp.data.data
    yield put(DisplayActions.getDisplaysInfoSuccess(displays))

  } else {

    let errors

    if (resp.problem === 'CLIENT_ERROR') {
      errors = [resp.data.message]
    }
    else {
      errors = [resp.problem]
    }

    yield put(DisplayActions.getDisplaysInfoRequestFailure(errors))
  }
}

export function* getExcelDisplaysRequest(api, action) {
  const { params } = action
  const resp = yield call(api.getExcelDisplays, params)

  if (resp.ok) {
    const excel = resp.data
    yield put(DisplayActions.getExcelDisplaysSuccess(excel))

  } else {
    let errors
    if (resp.problem === 'CLIENT_ERROR') {
      errors = [resp.data.message]
    }
    else {
      errors = [resp.problem]
    }

    yield put(DisplayActions.getExcelDisplaysFailure(errors))
  }
}

export function* updateDefaultContentRequest(api, action) {
  const { id } = action

  const resp = yield call(api.getContent, id)

  if (resp.ok) {
    const content = resp.data

    yield put(DisplayActions.updateDefaultContentSuccess(content))
  } else {
    let errors

    if (resp.problem === 'CLIENT_ERROR') {
      errors = [resp.data.message]
    }
    else {
      errors = [resp.problem]
    }

    yield put(DisplayActions.updateDefaultContentFailure(errors))
  }
}

export function* updateCustomContentRequest(api, action) {
  const { id, displayId, relationId, typePreview } = action

  let resp = yield call(api.getContent, id)

  if (resp.ok) {
    const content = resp.data

    yield put(DisplayActions.updateCustomContentSuccess(content, displayId, relationId, typePreview))

    let keepUpdating = !content.ready
    while (keepUpdating) {
      resp = yield call(api.getContent, id)
      if (get(resp, 'data.ready') || !resp.ok) {
        const content = resp.data
        yield put(DisplayActions.updateCustomContentSuccess(content, displayId, relationId, typePreview))
        return
      } else {
        yield delay(3000)
      }
    }

  } else {
    let errors

    if (resp.problem === 'CLIENT_ERROR') {
      errors = [resp.data.message]
    }
    else {
      errors = [resp.problem]
    }

    yield put(DisplayActions.updateCustomContentFailure(errors))
  }
}

export function* updateRelationCampaignRequest(api, action) {
  const { relation, content, campaignId } = action
  if (relation.belong_to_smart_campaign && relation.payment_status !== 'pending') {
    var newRelation = {
      "display_id": relation.display_id,
      "content_id": Number(content.id),
      "fill_screen": relation.fill_screen,
      "promised_shows": relation.promised_shows,
      "file_thumb": content.file_thumb
    }
  } else {
    var newRelation = {
      "display_id": relation.display_id,
      "content_id": Number(content),
      "fill_screen": relation.fill_screen,
    }
  }

  let response = yield call(api.updateCampaignContentDisplay, relation.id, newRelation, campaignId)
  if (response.ok) {
    yield put(DisplayActions.updateContentCampaignSuccess(content, relation.id))
    yield put(DisplayActions.getCampaignRequest(campaignId))
  } else {
    let errors
    if (response.problem === 'CLIENT_ERROR') {
      errors = [response.data.message]
    }
    else {
      errors = [response.problem]
    }
    yield put(DisplayActions.updateContentCampaignFailure(errors))
  }

}

export function* updateContentCampaignRequest(api, action) {
  const { content, relation, campaignId } = action

  if (relation.belong_to_smart_campaign) {
    var newRelation = {
      "display_id": relation.display_id,
      "content_id": content.id,
      "fill_screen": relation.fill_screen,
      "promised_shows": relation.promised_shows,
      // "rules": relation.rules
    }
  } else {
    var newRelation = {
      "display_id": relation.display_id,
      "content_id": content.id,
      "fill_screen": relation.fill_screen,
      // "rules": relation.rules
    }
  }

  let resp = yield call(api.getContent, content.id)

  if (resp.data.type === 'image') {
    let response = yield call(api.updateCampaignContentDisplay, relation.id, newRelation, campaignId)
    if (response.ok) {
      yield put(DisplayActions.updateContentCampaignSuccess(resp.data, relation.id))
    } else {
      let errors
      if (response.problem === 'CLIENT_ERROR') {
        errors = [response.data.message]
      }
      else {
        errors = [response.problem]
      }
      yield put(DisplayActions.updateContentCampaignFailure(errors))
    }
  } else if (resp.data.type === 'video') {
    if (resp.data.ready) {

      yield put(DisplayActions.createNewContentSuccess(resp.data, relation.id))
      let response = yield call(api.updateCampaignContentDisplay, relation.id, newRelation, campaignId)

      if (response.ok) {
        yield put(DisplayActions.updateContentCampaignSuccess(resp.data, relation.id))
      } else {
        let errors
        if (response.problem === 'CLIENT_ERROR') {
          errors = [response.data.message]
        }
        else {
          errors = [response.problem]
        }
        yield put(DisplayActions.updateContentCampaignFailure(errors))
      }
    } else {
      yield delay(3000)
    }
  }
}

export function* uploadFileRequest(api, action) {
  const { file } = action

  const resp = yield call(api.uploadFile, file)
  if (resp.ok) {
    const data = { resp }

    yield put(DisplayActions.uploadFileSuccess(data))
  } else {
    let errors

    if (resp.problem === 'CLIENT_ERROR') {
      errors = [resp.data.message]
    }
    else {
      errors = [resp.problem]
    }

    yield put(DisplayActions.uploadFileFailure(errors))
  }
}

export function* createCampaignRequest(api, action) {
  const { params } = action

  const resp = yield call(api.createCampaign, params)
  if (resp.ok) {
    const campaign = resp.data

    yield put(DisplayActions.createCampaignSuccess(campaign))
  } else {
    let errors

    if (resp.problem === 'CLIENT_ERROR') {
      errors = [resp.data.error]
    }
    else {
      errors = [resp.problem]
    }

    yield put(DisplayActions.createCampaignFailure(errors))
  }
}

const CAMPAIGN_ASYNC_OPERATION_POLL_MS = 5000

const asyncOperationActions = type =>
  type === 'update'
    ? {
      success: DisplayActions.editCampaignSuccess,
      processing: DisplayActions.editCampaignProcessing,
      asyncFailure: DisplayActions.editCampaignAsyncFailure,
      failure: data => DisplayActions.editCampaignFailure([data]),
    }
    : {
      success: DisplayActions.createCampaignSuccess,
      processing: DisplayActions.createCampaignProcessing,
      asyncFailure: DisplayActions.createCampaignAsyncFailure,
      failure: data => DisplayActions.createCampaignFailure([data.error || data.message]),
    }

function* pollCampaignAsyncOperation(api, campaignId, type) {
  const actions = asyncOperationActions(type)

  while (true) {
    yield delay(CAMPAIGN_ASYNC_OPERATION_POLL_MS)

    const resp = yield call(api.getCampaign, campaignId)
    if (!resp.ok) {
      if (resp.problem === 'CLIENT_ERROR') {
        yield put(actions.failure(resp.data))
        return
      }
      continue
    }

    const campaign = resp.data
    const asyncOperation = campaign.async_operation

    if (!asyncOperation || asyncOperation.status === 'completed') {
      yield put(actions.success(campaign))
      return
    }

    if (asyncOperation.status === 'failed') {
      yield put(actions.asyncFailure(asyncOperation))
      return
    }

    yield put(actions.processing(campaignId, asyncOperation))
  }
}

const getCampaignAsyncOperation = state => state.display.campaignAsyncOperation;

export function* retryCampaignAsyncOperationRequest(api, action) {
  const { campaignId, operationId } = action
  const asyncOperation = yield select(getCampaignAsyncOperation)

  const resp = yield call(api.retryCampaignAsyncOperation, campaignId, operationId)
  if (resp.ok) {
    yield call(pollCampaignAsyncOperation, api, campaignId, asyncOperation && asyncOperation.type)
  } else {
    let errors

    if (resp.problem === 'CLIENT_ERROR') {
      errors = [resp.data.message || resp.data.error]
    }
    else {
      errors = [resp.problem]
    }

    yield put(DisplayActions.retryCampaignAsyncOperationFailure(errors))
  }
}

const getCustomContents = state => state.display.customContents;

export function* editCampaignRequest(api, action, partial = false) {
  const { campaignId, params } = action;

  try {
    const customContents = yield select(getCustomContents);

    const replacements = customContents.filter(item =>
      item.relationId &&
      item.relationId.toString().startsWith('temp_') &&
      item.isReplacementAction
    );

    let cleanParams = JSON.parse(JSON.stringify(params));

    const updatePayloadWithRealId = (list, displayId, versionName, realId, newContentId) => {
      if (!Array.isArray(list)) return;
      const targetIndex = list.findIndex(item =>
        Number(item.display_id) === Number(displayId) &&
        item.content_version_name === versionName
      );

      if (targetIndex !== -1) {
        list[targetIndex].id = realId;
        list[targetIndex].relation_id = realId;
        if (newContentId) {
          list[targetIndex].content_id = newContentId;
        }
      }
    };

    const relationsList = replacements.map(item => ({
      content_display_id: Number(item.parentRelationId),
      display_id: Number(item.displayId),
      content_id: Number(item.content.id),
      type: 'complete_and_create_new_relation',
      white_label_name: 'latinad',
      fill_content: item.fill_screen ? 1 : 0,
      replicate_rules: true,
      content_version_name: item.content.content_version_name
    }));

    if (relationsList.length > 0) {

      const payload = { relations: relationsList };

      const response = yield call(api.changeProgrammaticContent, campaignId, payload);

      if (response.ok && Array.isArray(response.data)) {
        response.data.forEach((newRelation, index) => {

          const originalRequest = relationsList[index];

          if (originalRequest) {
            const versionName = originalRequest.content_version_name;
            const displayId = newRelation.display_id;
            const realId = newRelation.id;
            const newContentId = newRelation.content_id;

            if (cleanParams.campaign && cleanParams.campaign.contents_displays) {
              updatePayloadWithRealId(
                cleanParams.campaign.contents_displays,
                displayId,
                versionName,
                realId,
                newContentId
              );
            }

            if (cleanParams.contents_displays) {
              updatePayloadWithRealId(
                cleanParams.contents_displays,
                displayId,
                versionName,
                realId,
                newContentId
              );
            }
          }
        });
      } else {
        if (!response.ok) {
          let errors = response.problem === 'CLIENT_ERROR' ? [response.data] : [response.problem];
          yield put(DisplayActions.editCampaignFailure(errors));
          return;
        }
      }
    }

    if (partial) {
      yield call(patchCampaignContentsDisplays, api, campaignId, cleanParams);
      return;
    }

    const resp = yield call(api.editCampaign, campaignId, cleanParams);

    if (resp.ok) {
      const { campaign, async_operation: asyncOperation } = resp.data;

      if (resp.status === 202 && asyncOperation) {
        yield put(DisplayActions.editCampaignProcessing(campaignId, asyncOperation));
        yield call(pollCampaignAsyncOperation, api, campaignId, 'update');
        return;
      }

      yield put(DisplayActions.editCampaignSuccess(campaign));
    } else {
      let errors;
      if (resp.problem === 'CLIENT_ERROR') {
        errors = [resp.data];
      } else {
        errors = [resp.problem];
      }
      yield put(DisplayActions.editCampaignFailure(errors));
    }

  } catch (error) {
    console.error("Error in editCampaignRequest orchestration:", error);
    yield put(DisplayActions.editCampaignFailure(["An error occurred while processing ad changes."]));
  }
}

export function* patchCampaignContentsDisplaysRequest(api, action) {
  yield call(editCampaignRequest, api, action, true);
}

function* patchCampaignContentsDisplays(api, campaignId, params) {
  const resp = yield call(api.patchCampaignContentsDisplays, campaignId, params);

  if (resp.ok) {
    const { campaign, contents_displays: contentsDisplays } = resp.data;
    yield put(
      DisplayActions.patchCampaignContentsDisplaysSuccess(
        campaign,
        contentsDisplays || [],
        params.removed_display_ids || []
      )
    );
  } else {
    let errors;
    if (resp.problem === 'CLIENT_ERROR') {
      errors = [{ ...resp.data, status: resp.status }];
    } else {
      errors = [resp.problem];
    }
    yield put(DisplayActions.editCampaignFailure(errors));
  }
}

export function* archiveCampaignRequest(api, action) {
  const { campaignId, params } = action

  const resp = yield call(api.editCampaign, campaignId, params)
  if (resp.ok) {
    const campaign = resp.data
    yield put(DisplayActions.archiveCampaignSuccess(campaign.id))

  } else {
    let errors
    if (resp.problem === 'CLIENT_ERROR') {
      errors = [resp.data]
    }
    else {
      errors = [resp.problem]
    }
    yield put(DisplayActions.archiveCampaignFailure(errors))
  }
}

export function* getCampaignsRequest(api, action) {
  const { params } = action

  const resp = yield call(api.getCampaigns, params)
  if (resp.ok) {
    const campaigns = resp.data.data

    yield put(DisplayActions.getCampaignsSuccess(campaigns, resp.data.recordsFiltered))
  } else {
    let errors

    if (resp.problem === 'CLIENT_ERROR') {
      errors = [resp.data.message]
    }
    else {
      errors = [resp.problem]
    }

    yield put(DisplayActions.getCampaignsFailure(errors))
  }
}

export function* getCampaignRequest(api, action) {
  const { id } = action

  const resp = yield call(api.getCampaign, id)
  if (resp.ok) {
    const campaign = resp.data

    yield put(DisplayActions.getCampaignSuccess(campaign))
  } else {
    let errors

    if (resp.problem === 'CLIENT_ERROR') {
      errors = [resp.data.message]
    }
    else {
      errors = [resp.problem]
    }

    yield put(DisplayActions.getCampaignFailure(errors))
  }
}

export function* createPlannerRequest(api, action) {
  const { params } = action;
  const resp = yield call(api.createPlanner, params.plannerData);

  if (resp.ok) {
    const planner = resp.data;
    yield put(DisplayActions.createPlannerSuccess(planner));
    yield put(DisplayActions.toggleCampaignFormModal(true));
  } else {
    let errors;

    if (resp.problem === 'CLIENT_ERROR') {
      errors = [resp.data.message];
    }
    else {
      errors = [resp.problem];
    }
    yield put(DisplayActions.createPlannerFailure(errors));
  }
}

export function* justifyPlannerRequest(api, action) {
  const { params } = action;
  const resp = yield call(api.justifyPlanner, params);
  if (resp.ok) {
    yield put(DisplayActions.justifyPlannerSuccess(resp.data.justification));
  } else {
    const errors = resp.problem === 'CLIENT_ERROR' ? [resp.data.message] : [resp.problem];
    yield put(DisplayActions.justifyPlannerFailure(errors));
  }
}

export function* optimizeBriefRequest(api, action) {
  const { params } = action;
  const resp = yield call(api.optimizeBrief, params);

  if (resp.ok) {
    yield put(DisplayActions.optimizeBriefSuccess(resp.data));
  } else {
    let errors;
    if (resp.problem === 'CLIENT_ERROR') {
      errors = [resp.data.message];
    } else {
      errors = [resp.problem];
    }
    yield put(DisplayActions.optimizeBriefFailure(errors));
  }
}

export function* optimizeRulesRequest(api, action) {
  const { params } = action;
  const resp = yield call(api.optimizeRules, params);

  if (resp.ok) {
    yield put(DisplayActions.optimizeRulesSuccess(resp.data));
  } else {
    const errors = resp.problem === 'CLIENT_ERROR' ? [resp.data.message] : [resp.problem];
    yield put(DisplayActions.optimizeRulesFailure(errors));
  }
}

export function* createProposalsRequest(api, action) {
  const { plannerId, params } = action;
  const display = yield select(state => state.display);
  const user = yield select(state => state.userAccount.user);
  const formData = buildProposalFormData(params.user_modified_json, display.campaign_screens);
  const lastPlannerRequest = display.lastPlannerRequest || {};
  const isMediaOwner = !!(user && user.roles && (hasAccessRole(user.roles, [UserRoleEnum.companyMember]) || user.is_in_media_holding));

  const body = {
    planner_run_id: plannerId,
    brief: lastPlannerRequest.brief || '',
    brand_url: lastPlannerRequest.brand_url || '',
    client_name: (display.client && display.client.name) || '',
    brand_name: (display.brand && display.brand.name) || '',
    dsp_name: getNameWhiteLabel(getDomain(window.location.hostname)),
    only_MO_displays: isMediaOwner ? !display.showAllDisplays : false,
    form_data: formData,
  };

  const resp = yield call(createCampaignProposals, body);

  if (resp.ok) {
    const proposals = Array.isArray(resp.data) ? resp.data : (resp.data && resp.data.proposals) || [];
    yield put(DisplayActions.createProposalsSuccess({ proposals }));
  } else {
    const errors = resp.data && resp.data.message ? [resp.data.message] : [resp.problem];
    const code = resp.data && resp.data.code;
    yield put(DisplayActions.createProposalsFailure(errors, code));
  }
}

function buildProposalFormData(plannerFormAI, campaignScreensCatalog) {
  const catalog = campaignScreensCatalog || [];
  const screenSubtypes = (plannerFormAI.screen_subtypes || []).map(name => {
    const match = catalog.find(screen => screen.name_en === name);
    return { name, parent: (match && match.parent_latinad) || '' };
  });

  return { ...plannerFormAI, screen_subtypes: screenSubtypes };
}

export function* getCampaignCategoriesRequest(api, action) {
  const { params } = action;

  const resp = yield call(api.getCampaignCategories, params);

  if (resp.ok) {
    const campaign_categories = resp.data;
    yield put(DisplayActions.getCampaignCategoriesSuccess(campaign_categories));
  } else {
    let errors;
    if (resp.problem === 'CLIENT_ERROR') {
      errors = [resp.data.message];
    } else {
      errors = [resp.problem];
    }
    yield put(DisplayActions.getCampaignCategoriesFailure(errors));
  }
}

export function* getCampaignPOIRequest(api, action) {
  const { params } = action;

  const resp = yield call(api.getCampaignPOI, params);

  if (resp.ok) {
    const campaign_POI = resp.data;
    yield put(DisplayActions.getCampaignPOISuccess(campaign_POI));
  } else {
    let errors;
    if (resp.problem === 'CLIENT_ERROR') {
      errors = [resp.data.message];
    } else {
      errors = [resp.problem];
    }
    yield put(DisplayActions.getCampaignPOIFailure(errors));
  }
}

export function* getCampaignScreensRequest(api, action) {
  const { params } = action;

  const resp = yield call(api.getCampaignScreens, params);

  if (resp.ok) {
    const campaign_screens = resp.data;
    yield put(DisplayActions.getCampaignScreensSuccess(campaign_screens));
  } else {
    let errors;
    if (resp.problem === 'CLIENT_ERROR') {
      errors = [resp.data.message];
    } else {
      errors = [resp.problem];
    }
    yield put(DisplayActions.getCampaignScreensFailure(errors));
  }
}

export function* getClientsRequest(api, action) {
  const { userId } = action

  const resp = yield call(api.getClients, userId)
  if (resp.ok) {
    const clients = resp.data

    yield put(DisplayActions.getClientsSuccess(clients))
  } else {
    let errors

    if (resp.problem === 'CLIENT_ERROR') {
      errors = [resp.data.message]
    }
    else {
      errors = [resp.problem]
    }

    yield put(DisplayActions.getClientsFailure(errors))
  }
}

export function* getExternalDspsRequest(api) {
  const resp = yield call(api.getExternalDsps)
  if (resp.ok) {
    yield put(DisplayActions.getExternalDspsSuccess(resp.data))
  } else {
    let errors

    if (resp.problem === 'CLIENT_ERROR') {
      errors = [resp.data.message]
    }
    yield put(DisplayActions.getExternalDspsFailure(errors))
  }
}

export function* deleteCampaignRequest(api, action) {
  const { campaignId } = action

  const resp = yield call(api.deleteCampaign, campaignId)
  if (resp.ok) {
    yield put(DisplayActions.deleteCampaignSuccess(resp.data, campaignId))
  } else {
    yield put(DisplayActions.deleteCampaignFailure(resp.data))
  }
}

export function* createReplicateCampaignRequest(api, action) {
  const { campaignId, params } = action

  const resp = yield call(api.createReplicateCampaing, campaignId, params)
  if (resp.ok) {
    const campaign = resp.data

    yield put(DisplayActions.createReplicateCampaignSuccess(campaign.id))

  } else {
    yield put(DisplayActions.createReplicateCampaignFailure(resp.data))
  }
}

export function* requestSpecialCpmRequest(api, action) {
  const { displayId, requestedPrice, currency } = action;

  const resp = yield call(api.requestSpecialCpm, displayId, requestedPrice, currency)
  if (resp.ok) {
    yield put(DisplayActions.requestSpecialCpmSuccess())
  } else {
    yield put(DisplayActions.requestSpecialCpmFailure(resp.data))
  }
}

export function* requestSpecialPriceRequest(api, action) {
  const { displayId, requestedPrice, currency } = action;

  const resp = yield call(api.requestSpecialPrice, displayId, requestedPrice, currency)
  if (resp.ok) {
    yield put(DisplayActions.requestSpecialPriceSuccess())
  } else {
    yield put(DisplayActions.requestSpecialPriceFailure(resp.data))
  }
}


export function* getBrandsRequest(api, action) {
  const { clientId } = action

  const resp = yield call(api.getBrands, clientId)
  if (resp.ok) {
    const brands = resp.data

    yield put(DisplayActions.getBrandsSuccess(brands))
  } else {
    let errors

    if (resp.problem === 'CLIENT_ERROR') {
      errors = [resp.data.message]
    }
    else {
      errors = [resp.problem]
    }

    yield put(DisplayActions.getBrandsFailure(errors))
  }
}

export function* getQuotesRequest(api, action) {
  const { userId, params } = action

  const resp = yield call(api.getQuotes, userId, params)
  if (resp.ok) {
    const quotes = resp.data

    yield put(DisplayActions.getQuotesSuccess(quotes.reverse()))
  } else {
    let errors

    if (resp.problem === 'CLIENT_ERROR') {
      errors = [resp.data.message]
    }
    else {
      errors = [resp.problem]
    }

    yield put(DisplayActions.getQuotesFailure(errors))
  }
}

export function* setArchivedQuoteRequest(api, action) {
  const { quoteId, archived } = action

  const resp = yield call(api.setArchivedQuote, quoteId, archived)

  if (resp.ok) {
    yield put(DisplayActions.setArchivedQuoteSuccess(resp.data))
  } else {
    yield put(DisplayActions.setArchivedQuoteFailure(resp.data))
  }
}

export function* getQuoteRequest(api, action) {
  const { token, cart } = action

  const resp = yield call(api.getQuote, token)

  if (resp.ok) {
    const quote = resp.data

    for (var i = 0; i < cart.length; i++) {
      yield put(DisplayActions.removeDisplayFromCart(cart[i].id))
    }
    for (var i = 0; i < quote.displays.length; i++) {
      yield put(DisplayActions.addDisplayToCart(quote.displays[i]))
    }
    let id_dsp = quote.external_dsp ? quote.external_dsp.id : null;
    history.replace(`/map?currency=${quote.currency}&external_dsp_id=${id_dsp}&date_from=${quote.from_date}&date_to=${quote.to_date}&lat_ne=${quote.displays[0].latitude}&lat_sw=${quote.displays[0].latitude}&lng_ne=${quote.displays[0].longitude}&lng_sw=${quote.displays[0].longitude}&quote_profit=${quote.profit}&page=1&shows_per_day_min=1&is_quote=true&is_circuit=0`);

    yield put(DisplayActions.getQuoteSuccess(quote))
  } else {
    let errors

    if (resp.problem === 'CLIENT_ERROR') {
      errors = [resp.data.message]
    }
    else {
      errors = [resp.problem]
    }

    yield put(DisplayActions.getQuoteFailure(errors))
  }
}

export function* createQuoteRequest(api, action) {
  const { quoteData } = action

  const resp = yield call(api.createQuote, quoteData)
  if (resp.ok) {
    yield put(DisplayActions.createQuoteSuccess(resp.data))
  } else {
    yield put(DisplayActions.createQuoteFailure(resp.data))
  }
}

export function* editQuoteRequest(api, action) {
  const { quoteId, quoteData } = action
  const resp = yield call(api.editQuote, quoteId, quoteData)
  if (resp.ok) {
    yield put(DisplayActions.editQuoteSuccess(resp.data))
  } else {
    yield put(DisplayActions.editQuoteFailure(resp.data))
  }
}

export function* deleteQuoteRequest(api, action) {
  const { quoteId } = action

  const resp = yield call(api.deleteQuote, quoteId)
  if (resp.ok) {
    yield put(DisplayActions.deleteQuoteSuccess(resp.data))
  } else {
    yield put(DisplayActions.deleteQuoteFailure(resp.data))
  }
}

export function* createCouponRequest(api, action) {
  const { campaignId, code } = action

  const resp = yield call(api.createCoupon, campaignId, code)
  if (resp.ok) {
    const couponData = resp.data

    yield put(DisplayActions.createCouponSuccess(couponData))
  } else {
    let errors

    if (resp.problem === 'CLIENT_ERROR') {
      errors = [resp.data.message]
    }
    else {
      errors = [resp.problem]
    }
    yield put(DisplayActions.createCouponFailure(errors))
  }
}

export function* deleteCouponRequest(api, action) {
  const { campaignId, couponId } = action

  const resp = yield call(api.deleteCoupon, campaignId, couponId)
  if (resp.ok) {
    const couponData = resp.data

    yield put(DisplayActions.deleteCouponSuccess(couponData))
  } else {
    let errors

    if (resp.problem === 'CLIENT_ERROR') {
      errors = [resp.data.message]
    }
    else {
      errors = [resp.problem]
    }
    yield put(DisplayActions.deleteCouponFailure(errors))
  }
}

// export function * getMercadoPagoPaymentMethodsRequest (api, action) {
//   const resp = yield call(api.getMercadoPagoPaymentMethods)

//   if (resp.ok) {
//     yield put(DisplayActions.getMercadoPagoPaymentMethodsSuccess(resp.data))
//   } else {
//     yield put(DisplayActions.getMercadoPagoPaymentMethodsFailure(resp.data))
//   }
// }

export function* createPaymentRequest(api, action) {
  const { campaignId, paymentData } = action

  const resp = yield call(api.createPayment, campaignId, paymentData)

  if (resp.ok) {
    yield put(DisplayActions.createPaymentSuccess(resp.data))
  } else {
    yield put(DisplayActions.createPaymentFailure(resp.data))
  }
}

export function* getCampaignPaymentsRequest(api, action) {
  const { campaignId } = action

  const resp = yield call(api.getCampaignPayments, campaignId)

  if (resp.ok) {
    yield put(DisplayActions.getCampaignPaymentsSuccess(resp.data))
  } else {
    yield put(DisplayActions.getCampaignPaymentsFailure(resp.data))
  }
}

export function* getCampaignStatsRequest(api, action) {
  const { campaignId } = action

  const resp = yield call(api.getCampaignStats, campaignId)

  if (resp.ok) {
    yield put(DisplayActions.getCampaignStatsSuccess(resp.data))
  } else {
    yield put(DisplayActions.getCampaignStatsFailure(resp.data))
  }
}

export function* getPaymentRequest(api, action) {
  const { paymentId } = action

  const resp = yield call(api.getPayment, paymentId)

  if (resp.ok) {
    yield put(DisplayActions.getPaymentSuccess(resp.data))
  } else {
    yield put(DisplayActions.getPaymentFailure(resp.data))
  }
}

export function* deleteCampaignPaymentRequest(api, action) {
  const { paymentId } = action

  const resp = yield call(api.deleteCampaignPayment, paymentId)

  if (resp.ok) {
    yield put(DisplayActions.deletePaymentSuccess(resp.data))
  } else {
    yield put(DisplayActions.deletePaymentFailure(resp.data))
  }
}

export function* getMpInitPointRequest(api, action) {
  const { campaignId, paymentData } = action

  const resp = yield call(api.getMpInitPoint, campaignId, paymentData)

  if (resp.ok) {
    yield put(DisplayActions.getMpInitPointSuccess(resp.data))
  } else {
    yield put(DisplayActions.getMpInitPointFailure(resp.data))
  }
}

export function* getReportRequest(api, action) {
  const { reportData } = action

  const resp = yield call(api.getReport, reportData)

  if (resp.ok) {
    yield put(DisplayActions.getReportSuccess(resp.data))
  } else {
    yield put(DisplayActions.getReportFailure(resp.data))
  }
}

export function* getProposalRequest(api, action) {
  const { proposalData } = action

  const resp = yield call(api.getProposal, proposalData)

  if (resp.ok) {
    yield put(DisplayActions.getProposalSuccess(resp.data))
  } else {
    yield put(DisplayActions.getProposalFailure(resp.data))
  }
}

export function* getSingleReportRequest(api, action) {
  const { params } = action

  const resp = yield call(api.getSingleReport, params)

  if (resp.ok) {
    yield put(DisplayActions.getSingleReportSuccess(resp.data))
  } else {
    yield put(DisplayActions.getSingleReportFailure(resp.data))
  }
}

export function* getLastBillingInfoRequest(api, action) {
  const { userId } = action

  const resp = yield call(api.getLastBillingInfo, userId)

  if (resp.ok) {
    yield put(DisplayActions.getLastBillingInfoSuccess(resp.data))
  } else {
    yield put(DisplayActions.getLastBillingInfoFailure(resp.data))
  }
}

export function* getAudienceRequest(api, action) {
  const { data } = action

  const resp = yield call(api.getAudience, data)
  if (resp.ok) {
    const audience = resp.data

    yield put(DisplayActions.getAudienceSuccess(audience))
  } else {
    let errors

    if (resp.problem === 'CLIENT_ERROR') {
      errors = [resp.data.message]
    }
    else {
      errors = [resp.problem]
    }

    yield put(DisplayActions.getAudienceFailure(errors))
  }
}

export function* getAudienceDatsWhyRequest(api, action) {
  let { data } = action;
  // TO DO: prepared data when is more than one display, because {id: data.display_ids} hope is one id
  data = { "display_ids": data }
  const resp = yield call(api.getAudienceDatsWhy, data);

  if (resp.ok) {
    const audience = resp.data;
    yield put(DisplayActions.getAudienceDatsWhySuccess({ id: data.display_ids, data: audience }));
  } else {
    let errors;

    if (resp.problem === "CLIENT_ERROR") {
      errors = [resp.data.message];
    } else {
      errors = [resp.problem];
    }
    yield put(DisplayActions.getAudienceDatsWhyFailure(errors, { id: data, data: null }));
  }
}

export function* createPurchaseOrdersRequest(api, action) {
  const { companyId, campaignId } = action

  const resp = yield call(api.createPurchaseOrders, companyId, campaignId)

  if (resp.ok) {
    yield put(DisplayActions.createPurchaseOrdersSuccess(resp.data))
  } else {
    yield put(DisplayActions.createPurchaseOrdersFailure("Error"))
  }
}

export function* getCurrencyExchangeRateRequest(api) {

  const resp = yield call(api.getCurrencyExchangeRate)

  if (resp.ok) {
    yield put(DisplayActions.getCurrencyExchangeRateSuccess(resp.data))
  } else {
    yield put(DisplayActions.getCurrencyExchangeRateFailure("Error"))
  }
}

export function* getConversionRatesRequest(api) {
  const resp = yield call(api.getConversionRates)

  if (resp.ok) {
    yield put(DisplayActions.getConversionRatesSuccess(resp.data))
  } else {
    yield put(DisplayActions.getConversionRatesFailure("Error"))
  }
}

export function* getCategoriesRequest(api) {
  const resp = yield call(api.getCategories)

  if (resp.ok) {
    yield put(DisplayActions.getCategoriesSuccess(resp.data))
  } else {
    yield put(DisplayActions.getCategoriesFailure("Error"))
  }
}

export function* getContentsByCompanyRequest(api, action) {
  const { companyId, params } = action

  const resp = yield call(api.getContents, companyId, params)
  if (resp.ok) {
    yield put(DisplayActions.getContentsByCompanySuccess(resp.data))
  } else {
    yield put(DisplayActions.getContentsByCompanyFailure("Error"))
  }
}

export function* getContentsByUsersRequest(api, action) {
  const { userId, params } = action

  const resp = yield call(api.getContentsByUsers, userId, params)
  if (resp.ok) {
    yield put(DisplayActions.getContentsByUsersSuccess(resp.data))
  } else {
    yield put(DisplayActions.getContentsByUsersFailure("Error"))
  }
}

export function* getAudienceHoursOfDayRequest(api, action) {
  let { data } = action;
  const params = {
    display_ids: data.display_ids,
    days: data.days,
  };
  const resp = yield call(api.getAudienceHoursOfDay, params);

  if (resp.ok) {
    const audience = resp.data;
    yield put(DisplayActions.getAudienceHoursOfDaySuccess({ id: data.display_ids, data: audience }));
  } else {
    yield put(DisplayActions.getAudienceHoursOfDayFailure({ id: data, data: null }));
  }
}

export function* getAudienceDaysOfWeekRequest(api, action) {
  let { data } = action;
  data = { "display_ids": data }
  const resp = yield call(api.getAudienceDaysOfWeek, data);

  if (resp.ok) {
    const audience = resp.data;
    yield put(DisplayActions.getAudienceDaysOfWeekSuccess({ id: data.display_ids, data: audience }));
  } else {
    yield put(DisplayActions.getAudienceDaysOfWeekFailure({ id: data, data: null }));
  }
}

export function* getAudienceQualitativeRequest(api, action) {
  const { displayId } = action;
  const resp = yield call(api.getAudienceQualitative, displayId);

  if (resp.ok) {
    const audienceQualitative = resp.data;
    yield put(DisplayActions.getAudienceQualitativeSuccess({ id: displayId, data: audienceQualitative }));
  } else {
    let errors;

    if (resp.problem === 'CLIENT_ERROR') {
      errors = [resp.data.message];
    }
    else {
      errors = [resp.problem];
    }

    yield put(DisplayActions.getAudienceQualitativeFailure(errors, { id: displayId, data: null }));
  }
}

export function* getProposalAudienceRequest(api, action) {
  const { data } = action;

  const resp = yield call(api.getProposalAudience, data);

  if (resp.ok) {
    yield put(DisplayActions.getProposalAudienceSuccess(resp.data));
  } else {
    let errors;

    if (resp.problem === 'CLIENT_ERROR') {
      errors = [resp.data.message];
    } else {
      errors = [resp.problem];
    }

    yield put(DisplayActions.getProposalAudienceFailure(errors));
  }
}

export function* createCampaignCommentRequest(api, action) {
  const { campaignId, comments } = action;

  const resp = yield call(api.createCampaignComment, campaignId, comments);
  if (resp.ok) {
    yield put(DisplayActions.createCampaignCommentSuccess(resp.data));
  } else {
    let errors;

    if (resp.problem === 'CLIENT_ERROR') {
      errors = [resp.data.message];
    } else {
      errors = [resp.problem];
    }

    yield put(DisplayActions.createCampaignCommentFailure(errors));
  }
}

export function* editCampaignCommentRequest(api, action) {
  const { campaignId, comments } = action;

  const resp = yield call(api.editCampaignComment, campaignId, comments);

  const hasError = resp.data.errors.length > 0;

  if (resp.ok && !hasError) {
    yield put(DisplayActions.editCampaignCommentSuccess(resp.data));
  } else {
    let errors;

    if (resp.problem === 'CLIENT_ERROR') {
      errors = [resp.data.message];
    } else {
      errors = [resp.problem];
    }

    if (hasError) {
      errors = resp.data.errors;
    }

    yield put(DisplayActions.editCampaignCommentFailure(errors));
  }
}

export function* editCampaignDateRequest(api, action) {
  const { campaignId, params } = action;
  const resp = yield call(api.editCampaignDate, campaignId, params);

  if (resp.ok) {
    if (resp.data.update_status.dates == "error") {
      yield put(DisplayActions.editCampaignDateFailure([resp.data.errors.dates]));
    } else {
      yield put(DisplayActions.editCampaignDateSuccess(resp.data));
    }
  } else {
    let errors;

    if (resp.problem === 'CLIENT_ERROR') {
      errors = [resp.data.message];
    } else {
      errors = [resp.problem];
    }

    yield put(DisplayActions.editCampaignDateFailure(errors));
  }
}


export function* changeProgrammaticContentRequest(api, action) {
  try {
    const { campaignId, params } = action;
    
    const sanitizedParams = {
      ...params,
      display_id: Number(params.display_id),
      content_id: Number(params.content_id),
      replicate_rules: Boolean(params.replicate_rules),
      fill_content: Boolean(params.fill_content)
    };
    
    if (params.content_display_id) {
      sanitizedParams.content_display_id = Number(params.content_display_id);
    }

    const response = yield call(api.changeProgrammaticContent, campaignId, sanitizedParams);

    if (response.ok) {
        yield put({
            type: 'CHANGE_PROGRAMMATIC_CONTENT_SUCCESS',
            contentDisplay: response.data
        });

        const campaignResp = yield call(api.getCampaign, campaignId);

        if (campaignResp.ok) {
            const campaign = campaignResp.data;
            yield put(DisplayActions.getCampaignSuccess(campaign));
            if (campaign.contents_displays) {
                const newCustomContents = campaign.contents_displays.map(item => ({
                    relationId: item.id,
                    displayId: item.display_id,
                    content: {
                        id: item.content_id,
                        name: item.content_name,
                        width: item.content_width,
                        height: item.content_height,
                        file: item.content_file,
                        file_thumb: item.content_file_thumb,
                        // Same as the edit-mode projection: the relation has no
                        // preview_file, so a dynamic ad falls back to its thumbnail.
                        preview_file: item.content_file_thumb,
                        type: item.content_type,
                        content_version_name: item.content_version_name,
                        duration: item.duration || item.length,
                        isPlaceholder: item.revision_status === 'pending_content',
                        ready: true 
                    },
                    active: item.active,
                    fill_screen: item.fill_screen,
                    promised_shows: item.promised_shows,
                    promised_shows_subsidized: item.promised_shows_subsidized,
                    subsidized_price: item.subsidized_price,
                    rules: item.rules ? (typeof item.rules === 'string' ? JSON.parse(item.rules) : item.rules) : [],
                    revision_status: item.revision_status,
                    isPendingContent: item.revision_status === 'pending_content',
                    isContentMixed: false, 
                    isFixedBudget: false 
                }));
                yield put(DisplayActions.updateCustomContents(newCustomContents));
                const newListOfContents = newCustomContents.map(cc => cc.content);
                yield put(DisplayActions.setListOfContentsInCampaign(newListOfContents));
            }
        }
    } else {
      yield put({
        type: 'CHANGE_PROGRAMMATIC_CONTENT_FAILURE',
        errors: response.data
      });
    }
  } catch (error) {
    yield put({
      type: 'CHANGE_PROGRAMMATIC_CONTENT_FAILURE',
      errors: error
    });
  }
}

export function* getPlaceholdersRequest(api, action) {
  const { listResolution } = action;

  const resp = yield call(api.getPlaceholders, listResolution);

  if (resp.ok) {
    yield put(DisplayActions.getPlaceholdersSuccess(resp.data));
  } else {
    yield put(DisplayActions.getPlaceholdersFailure(resp.data));
  }
}

export function* getCampaignAudienceAttributes(api, action) {
  const { displayIds } = action;

  if (!displayIds || displayIds.length === 0) {
    yield put(DisplayActions.getCampaignAudienceAttributesSuccess([]));
    return;
  }

  try {
    const resp = yield call(api.getAudienceQualitativeBulk, displayIds);

    if (resp.ok) {
      yield put(DisplayActions.getCampaignAudienceAttributesSuccess(resp.data));
    } else {
      yield put(DisplayActions.getCampaignAudienceAttributesFailure(resp.data));
    }
  } catch (e) {
    yield put(DisplayActions.getCampaignAudienceAttributesFailure(e));
  }
}

export function* calculateCampaignPricesRequest(api, action) {
  const { params } = action;

  const resp = yield call(api.calculateCampaignPrices, params);

  if (resp.ok) {
    yield put(DisplayActions.calculateCampaignPricesSuccess(resp.data));
  } else {
    let errors;
    if (resp.problem === 'CLIENT_ERROR') {
      errors = [resp.data.message || resp.data];
    } else {
      errors = [resp.problem];
    }
    yield put(DisplayActions.calculateCampaignPricesFailure(errors, resp.status));
  }
}

export function* getMediaCompaniesRequest(api, action) {
  const { params } = action;
  const resp = yield call(api.getMediaCompanies, params);

  if (resp.ok) {
    yield put(DisplayActions.getMediaCompaniesSuccess(resp.data));
  } else {
    let errors;
    if (resp.problem === 'CLIENT_ERROR') {
      errors = [resp.data.message];
    } else {
      errors = [resp.problem];
    }
    yield put(DisplayActions.getMediaCompaniesFailure(errors));
  }
}